import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Users,
  Folder,
  Bot,
  ChevronDown,
  Bug,
  ExternalLink,
  Video,
} from 'lucide-react'
import CallConfigModal from './CallConfigModal'
import ProfilePicture from './ProfilePicture'
import LanguageSwitcher from './LanguageSwitcher'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAppStore } from '@/store'
import { useTranslation } from 'react-i18next'
import { colors } from '@/styles/colors'
import { useBugReport } from '@/components/bug-report/useBugReport'
import { isAramcoEnv } from '@/config/environment'
import HelpAgentLauncherSlot from '@/components/HelpAgent/HelpAgentLauncherSlot'

type DataMenuItem = {
  icon: JSX.Element
  name: string
  path: string
  comingSoon?: boolean
}

type DataMenuSection = {
  section: string
  items: DataMenuItem[]
}

const Navigation = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, setSelectedAgentForCall } = useAppStore()
  const { openBugReport } = useBugReport()
  const [dataMenuOpen, setDataMenuOpen] = useState(false)
  const [langTooltipOpen, setLangTooltipOpen] = useState(false)
  const [langTooltipDisabled, setLangTooltipDisabled] = useState(false)
  const [accountTooltipOpen, setAccountTooltipOpen] = useState(false)
  const [accountTooltipDisabled] = useState(false)
  const [showCallModal, setShowCallModal] = useState(false)
  const dataMenuRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const DATA_MENU: DataMenuSection[] = [
    {
      section: t('nav:sections.data_onboarding'),
      items: [
        {
          icon: <Folder className="w-5 h-5" style={{ color: colors.violet[600] }} />,
          name: t('nav:menu.data_catalog'),
          path: '/data-catalog',
        },
        {
          icon: <Bot className="w-5 h-5" style={{ color: colors.violet[600] }} />,
          name: t('nav:menu.digital_coworker'),
          path: '/digital-coworker',
          comingSoon: isAramcoEnv,
        },
        {
          icon: (
            <img
              src="/images/icon-connectors.svg"
              alt={t('nav:menu.connectors')}
              style={{ width: 20, height: 20 }}
              className="inline-block align-middle"
            />
          ),
          name: t('nav:menu.connectors'),
          path: '/connectors',
        },
        {
          icon: <Users className="w-5 h-5" style={{ color: colors.violet[600] }} />,
          name: t('nav:menu.expert_knowledge'),
          path: '#',
          comingSoon: true,
        },
      ],
    },
    {
      section: t('nav:sections.data_contextualization'),
      items: [
        {
          icon: (
            <img
              src="/images/icon-pid.svg"
              alt={t('nav:menu.interactive_pids')}
              style={{ width: 18, height: 18 }}
              className="inline-block align-middle"
            />
          ),
          name: t('nav:menu.interactive_pids'),
          path: '#',
          comingSoon: true,
        },
        {
          icon: (
            <img
              src="/images/icon-hierarchy.svg"
              alt={t('nav:menu.asset_hierarchy')}
              style={{ width: 18, height: 18 }}
              className="inline-block align-middle"
            />
          ),
          name: t('nav:menu.asset_hierarchy'),
          path: '#',
          comingSoon: true,
        },
        {
          icon: (
            <img
              src="/images/icon-graph.svg"
              alt={t('nav:menu.knowledge_graph')}
              style={{ width: 18, height: 18 }}
              className="inline-block align-middle"
            />
          ),
          name: t('nav:menu.knowledge_graph'),
          path: '#',
          comingSoon: true,
        },
        // Polish Gap #18c: SME Binding Review relocated into the
        // `/settings/knowledge-graph` Bindings tab. Removed from the
        // customer-facing Data dropdown — SME-internal HITL surface
        // doesn't belong in the top nav.
      ],
    },
  ]

  const MAIN_NAV: Array<{ name: string; path: string; isExternal?: boolean; comingSoon?: boolean }> = [
    { name: t('nav:menu.intelligent_board'), path: '/intelligent-board' },
  ]

  // Close dropdown if clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dataMenuRef.current &&
        !dataMenuRef.current.contains(event.target as Node)
      ) {
        setDataMenuOpen(false)
      }
    }
    if (dataMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dataMenuOpen])

  return (
    <TooltipProvider>
      <nav className="flex justify-center p-4 border-b bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border">
        <div className="w-8/12 flex items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <img
              src="/images/intuai-logo.svg"
              width={140}
              alt="logo"
              draggable={false}
              className="mr-2 cursor-pointer hover:opacity-80 transition-opacity dark:hidden"
              onClick={() => navigate('/intelligent-board')}
            />
            <img
              src="/images/intuai-logo-dark.svg"
              width={140}
              alt="logo"
              draggable={false}
              className="mr-2 cursor-pointer hover:opacity-80 transition-opacity hidden dark:block"
              onClick={() => navigate('/intelligent-board')}
            />
            <div className="flex items-center gap-2">
              {/* Main Nav Items */}
              {MAIN_NAV.map(({ name, path, isExternal, comingSoon }) => {
                const button = (
                  <Button
                    className={`capitalize flex items-center gap-1 ${comingSoon ? 'opacity-50 cursor-not-allowed' : ''}`}
                    variant={!comingSoon && location.pathname.startsWith(path) ? 'secondary' : 'ghost'}
                    disabled={comingSoon}
                    onClick={() => {
                      if (comingSoon) return
                      if (isExternal) {
                        window.open(path, '_blank', 'noopener,noreferrer')
                      } else {
                        if (path !== '#') navigate(path)
                      }
                    }}
                  >
                    {name}
                    {isExternal && <ExternalLink className="w-3 h-3" />}
                  </Button>
                )

                if (comingSoon && !isAramcoEnv) {
                  return (
                    <Tooltip key={name}>
                      <TooltipTrigger asChild>
                        <span>{button}</span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p>{t('common:general.coming_soon')}</p>
                      </TooltipContent>
                    </Tooltip>
                  )
                }

                return <span key={name}>{button}</span>
              })}
            {/* Data Dropdown */}
            <div className="relative" ref={dataMenuRef}>
              <Button
                className="capitalize flex items-center gap-1"
                variant={
                  location.pathname.startsWith('/data') ? 'secondary' : 'ghost'
                }
                onClick={() => setDataMenuOpen((v) => !v)}
              >
                {t('nav:menu.data')}
                <ChevronDown
                  className="ml-1 text-gray-600 dark:text-gray-400"
                  style={{ width: 20, height: 20 }}
                />
              </Button>
              {dataMenuOpen && (
                <div className="absolute z-40 mt-2 min-w-[320px] bg-white dark:bg-dark-card shadow-xl rounded-2xl py-3 px-0 flex flex-col gap-2 border dark:border-dark-border">
                  {DATA_MENU.map(({ section, items }) => (
                    <div key={section} className="px-5 py-2">
                      <div className="text-xs font-semibold mb-2 ml-2 text-indigo-900 dark:text-indigo-300">
                        {section}
                      </div>
                      <div className="flex flex-col gap-1">
                        {items.map(({ icon, name, path, comingSoon }) => {
                          const menuItem = (
                            <button
                              className={`flex items-center gap-3 w-full py-2 px-2 rounded-lg transition text-left text-base font-normal ${
                                comingSoon ? 'opacity-50 cursor-not-allowed' : 'text-gray-900 dark:text-dark-text hover:bg-violet-50 dark:hover:bg-violet-900/20'
                              }`}
                              onClick={() => {
                                setDataMenuOpen(false)
                                if (path !== '#' && !comingSoon) navigate(path)
                              }}
                              tabIndex={0}
                              type="button"
                              disabled={comingSoon}
                            >
                              {icon}
                              {name}
                            </button>
                          )

                          if (comingSoon && !isAramcoEnv) {
                            return (
                              <Tooltip key={name}>
                                <TooltipTrigger asChild>
                                  <span className="block">{menuItem}</span>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>{t('common:general.coming_soon')}</p>
                                </TooltipContent>
                              </Tooltip>
                            )
                          }

                          return <div key={name}>{menuItem}</div>
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Tooltip
              open={langTooltipOpen && !langTooltipDisabled}
              onOpenChange={(open) => {
                if (!langTooltipDisabled) setLangTooltipOpen(open)
              }}
            >
              <TooltipTrigger asChild>
                <div>
                  <LanguageSwitcher
                    onOpenChange={(open) => {
                      setLangTooltipDisabled(open)
                      if (open) setLangTooltipOpen(false)
                    }}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{t('common:tooltips.change_language')}</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Bug
                  onClick={openBugReport}
                  className="w-8 cursor-pointer text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label={t('common:tooltips.report_bug')}
                />
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{t('common:tooltips.report_bug')}</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => {
                    setSelectedAgentForCall(undefined)
                    setShowCallModal(true)
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors text-gray-700 dark:text-gray-300"
                >
                  <Video className="w-5 h-5" />
                  <span className="text-sm font-medium">{t('common:navigation.call_ai_engineer')}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{t('common:tooltips.call_ai_engineer')}</p>
              </TooltipContent>
            </Tooltip>
            {/* TODO (INTUIAI-671): Add notifications */}
            {/* <Bell className="w-8 text-gray-400 hover:text-gray-600 cursor-pointer" /> */}
            <HelpAgentLauncherSlot />
            <Tooltip
              open={accountTooltipOpen && !accountTooltipDisabled}
              onOpenChange={(open) => {
                if (!accountTooltipDisabled) setAccountTooltipOpen(open)
              }}
            >
              <TooltipTrigger asChild>
                <button
                  onClick={() => navigate('/settings')}
                  // ProfilePicture's image is decorative (alt=""), and it is
                  // this button's ONLY child — so without a label the button
                  // has no accessible name (WCAG 4.1.2). The tooltip does not
                  // supply one: it contributes aria-describedby, a description
                  // rather than a name. Reuses the key the tooltip already
                  // shows, which beats announcing an email address.
                  aria-label={t('common:tooltips.account')}
                  className="cursor-pointer"
                >
                  <ProfilePicture user={user} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{t('common:tooltips.account')}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Call AI Engineer Modal */}
        <CallConfigModal
          isOpen={showCallModal}
          onClose={() => setShowCallModal(false)}
        />
      </nav>
    </TooltipProvider>
  )
}

export default Navigation
